import React, { useEffect, useMemo } from 'react';
import CardCommon from '@op/components/CardCommon';
import { RightInfo } from '@op/components/TableExpandableDetail/RightInfo';
import { LanguageType } from '@op/types/common';
import { useRequestStats } from '@op/pages/feeds/hooks/useFeedsRequest';
import ReviewsModal from '@op/components/ReviewsModal';
import useReviewModalData from '@op/components/ReviewsModal/useReviewModalData';
import { transLanguage } from '@op/utils';
import { useIntl } from 'umi';
import { summaryTopicPerformanceTips } from '@op/components/BubbleCommon';
import PositiveAndNegative from './PositiveAndNegative';
import { Keyword, OverviewChart } from '@op/components/ExpandablePlane';
import TopicLevelTwoTable from '../TopicLevelTwoTable';
import GPT, { useTopicPromptList } from '@op/components/GPT';
import { getOverviewTopicGptSummary } from '@op/services';

export default function TopicAnalysis({
  topics,
  filter,
  transTopic,
  language,
  topicLevel = 'L1',
  currentTopic,
  goToFeeds,
  gameInfo,
  sortData,
  topicList,
}: {
  topics: Array<string>;
  transTopic: any;
  filter: any;
  language: LanguageType;
  topicLevel?: 'L1' | 'L2';
  currentTopic: string;
  goToFeeds?: () => void;
  gameInfo?: any;
  sortData: (
    list: Array<any>,
    sort: { by: string; order: 'desc' | 'asc' },
  ) => Array<any>;
  topicList: any;
}) {
 
  const { formatMessage } = useIntl();

  const params = useMemo(() => {
    return {
      ...filter,
      topic: topics,
    };
  }, [filter]);

  const { feedsStats, requestState } = useRequestStats({});
  const sentimentCountMap = useMemo(() => {
    const sentimentCountMap = {
      positive: feedsStats?.good_total || 0,
      neutral: feedsStats?.neutral_total || 0,
      negative: feedsStats?.bad_total || 0,
      not_sentiment: feedsStats?.not_sentiment_total || 0,
    };

    return sentimentCountMap;
  }, [feedsStats]);

  useEffect(() => {
    requestState(params);
  }, [params]);

  const { reviewsVisible, data, openModal, toggleModal } = useReviewModalData();

  const getTopicPrompt = useTopicPromptList({
    topic: transTopic(currentTopic, topicLevel),
  });

  const GPTParams = useMemo(() => {
    return {
      ...params,
      topic_level: topicLevel,
    };
  }, []);

  const requestGptMap = useMemo(() => {
    return {
      defaultRequest: getOverviewTopicGptSummary as any,
      dealwithParams: ({
        edition_unified_id,
        end_time,
        entity_type,
        id_type,
        start_time,
        date_type,
      }: any) => {
        return {
          edition_unified_id,
          id_type,
          entity_type,
          date_type,
          start_time,
          end_time,
          topic_level: topicLevel,
          topic: currentTopic,
        };
      },
    };
  }, []);

  return (
    <CardCommon
      contentCard
      title={formatMessage({ id: 'Topic Analysis' })}
      className="dbo-topic-analysis-card card-slot"
    >
      <div className="dbo-topic-analysis">
        <div className="dbo-topic-analysis-left">
          <PositiveAndNegative
            filter={params}
            topicLevel={topicLevel}
            topics={topics}
            currentTopic={currentTopic}
          ></PositiveAndNegative>
          <OverviewChart
            filter={params}
            type="language"
            title="Language"
            translate={(text: string) => {
              return transLanguage(text);
            }}
          ></OverviewChart>
          <OverviewChart
            filter={params}
            type="channel"
            title="Channel"
          ></OverviewChart>
        </div>
        <div className="dbo-topic-analysis-right">
          <Keyword language={language} filter={params}></Keyword>

          <div className="dbo-topic-analysis-gpt">
            <RightInfo
              commonParams={{
                ...gameInfo,
                ...params,
                topic_level: topicLevel,
              }}
              isSubTopicSentiment={params.topic_sentiment_type === 'sub_topic'}
              sentimentCountMap={sentimentCountMap}
              language={language}
              hasGptPanel
              itemClick={(item) => {
                openModal(item);
              }}
              sortItem="engagement"
              tips={summaryTopicPerformanceTips}
            >
              <GPT.PageCard
                loadingHeight={560}
                filter={GPTParams}
                contentCard
                saveName="Topic Tracking"
                oriFeedsState={feedsStats}
                promptList={(game: any) => getTopicPrompt(game)}
                linkFilterChange={false}
                requestFuncMap={requestGptMap}
              >
                {(toggleVisible: () => any, { loading, total }: any) => (
                  <div className="dbo-topic-gpt-btn">
                    <GPT.Button
                      disabled={loading && total > 0}
                      onClick={() => toggleVisible()}
                    ></GPT.Button>
                  </div>
                )}
              </GPT.PageCard>
            </RightInfo>
          </div>
        </div>
      </div>
      <div>
        {topicLevel === 'L1' && (
          <TopicLevelTwoTable
            topics={topics}
            filter={filter}
            gameInfo={gameInfo}
            sortData={sortData}
            transTopic={transTopic}
            language={language}
            topicList={topicList}
            goToFeeds={goToFeeds}
          ></TopicLevelTwoTable>
        )}
      </div>
      <ReviewsModal
        visible={reviewsVisible}
        data={data}
        topics={topics}
        oneLevelTopic={topicLevel === "L1" ? transTopic(currentTopic, topicLevel) : undefined}
        topicList={topicList}
        language={language}
        close={toggleModal}
      />
    </CardCommon>
  );
}
